/**
 * Línea de tiempo (keen-slider) — layout `timeline` del page-section.
 * Markup: .hg-timeline > header + .hg-timeline__viewport > .keen-slider.hg-timeline__slider > slides.
 */

import KeenSlider from 'keen-slider'
import type { KeenSliderInstance, KeenSliderOptions } from 'keen-slider'
import 'keen-slider/keen-slider.min.css'

export default class Timeline {
	private wrapper: HTMLElement
	private sliderRoot: HTMLElement | null = null
	private slider: KeenSliderInstance | null = null
	private prevBtn: HTMLButtonElement | null = null
	private nextBtn: HTMLButtonElement | null = null
	private boundPrev: (() => void) | null = null
	private boundNext: (() => void) | null = null

	constructor(wrapper: HTMLElement) {
		this.wrapper = wrapper
	}

	init(): void {
		this.sliderRoot = this.wrapper.querySelector<HTMLElement>('.hg-timeline__slider')
		if (!this.sliderRoot) return

		const slideCount = this.sliderRoot.querySelectorAll('.keen-slider__slide').length
		if (slideCount < 2) return

		this.prevBtn = this.wrapper.querySelector<HTMLButtonElement>('.hg-timeline__nav--prev')
		this.nextBtn = this.wrapper.querySelector<HTMLButtonElement>('.hg-timeline__nav--next')

		this.slider = new KeenSlider(this.sliderRoot, this.getOptions())

		this.boundPrev = () => {
			this.slider?.prev()
		}
		this.boundNext = () => {
			this.slider?.next()
		}
		this.prevBtn?.addEventListener('click', this.boundPrev)
		this.nextBtn?.addEventListener('click', this.boundNext)
		window.addEventListener('resize', this.onResize)
	}

	cleanup(): void {
		this.wrapper.classList.remove('hg-timeline--dragging')
		window.removeEventListener('resize', this.onResize)
		if (this.boundPrev && this.prevBtn) {
			this.prevBtn.removeEventListener('click', this.boundPrev)
		}
		if (this.boundNext && this.nextBtn) {
			this.nextBtn.removeEventListener('click', this.boundNext)
		}
		this.boundPrev = null
		this.boundNext = null

		this.slider?.destroy()
		this.slider = null
	}

	private onResize = (): void => {
		if (!this.sliderRoot || !this.slider) return
		this.slider.update(this.getOptions())
	}

	private getOptions(): KeenSliderOptions {
		return {
			loop: false,
			mode: 'snap',
			rubberband: true,
			slideChanged: () => this.syncUi(),
			created: () => this.syncUi(),
			dragStarted: () => {
				this.wrapper.classList.add('hg-timeline--dragging')
			},
			dragEnded: () => {
				this.wrapper.classList.remove('hg-timeline--dragging')
			},
			slides: {
				perView: 1.25,
				spacing: 0,
			},
			breakpoints: {
				'(min-width: 768px)': {
					slides: {
						perView: 2,
						spacing: 0,
					},
				},
				'(min-width: 1200px)': {
					slides: {
						perView: 3,
						spacing: 0,
					},
				},
			},
		}
	}

	private syncUi = (): void => {
		const d = this.slider?.track?.details
		if (!d) return

		const atStart = d.rel <= d.minIdx
		const atEnd = d.rel >= d.maxIdx

		if (this.prevBtn) {
			this.prevBtn.disabled = atStart
			this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false')
		}
		if (this.nextBtn) {
			this.nextBtn.disabled = atEnd
			this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false')
		}
	}
}
